// pageInside/payments/WalletPayButton.jsx
//
// Apple Pay / Google Pay through Stripe's official Payment Request Button
// (the Stripe integration already used by the checkout). The wallet sheet
// returns a Stripe PaymentMethod; the parent sends it to Elakay, which
// verifies + attaches it server-side and runs the EXISTING order pipeline
// (server-computed amounts, one manual-capture PaymentIntent per line).
import React, { useEffect, useMemo, useRef, useState } from "react";
import { PaymentRequestButtonElement, useStripe } from "@stripe/react-stripe-js";
import { elakayAutoText } from "../dataElakay";

const OTHER_WALLETS = { applePay: ["googlePay", "link", "browserCard"], googlePay: ["applePay", "link", "browserCard"] };
// Detection must exclude Link and the browser's saved cards: when Link is
// available Stripe reports it INSTEAD of Google Pay (googlePay:false), which
// hid Google Pay even on devices that have it.
const DETECTION_DISABLED = ["link", "browserCard"];

export const buildRequest = (stripe, { amountCents, wallet }) => stripe.paymentRequest({
  country: "US",
  currency: "usd",
  total: { label: "Elakay", amount: Math.max(0, Math.round(amountCents || 0)) },
  requestPayerName: false,
  requestPayerEmail: false,
  disableWallets: wallet ? OTHER_WALLETS[wallet] : DETECTION_DISABLED,
});

/**
 * Which wallets this device/browser can really use (Stripe canMakePayment).
 * @returns {{ applePay: boolean, googlePay: boolean, checked: boolean }}
 */
export const useWalletAvailability = (amountCents, enabled) => {
  const stripe = useStripe();
  const [state, setState] = useState({ applePay: false, googlePay: false, checked: false });
  const hasAmount = Number(amountCents) > 0;
  useEffect(() => {
    if (!stripe || !enabled || !hasAmount) return undefined;
    let active = true;
    try {
      buildRequest(stripe, { amountCents }).canMakePayment().then((result) => {
        if (active) setState({ applePay: Boolean(result?.applePay), googlePay: Boolean(result?.googlePay), checked: true });
      }).catch(() => active && setState({ applePay: false, googlePay: false, checked: true }));
    } catch {
      setState({ applePay: false, googlePay: false, checked: true });
    }
    return () => { active = false; };
    // Availability depends on the device, not on the exact amount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe, enabled, hasAmount]);
  return state;
};

const WalletPayButton = ({ wallet, amountCents, onPaymentMethod }) => {
  const stripe = useStripe();
  const [available, setAvailable] = useState(null);
  const handlerRef = useRef(onPaymentMethod);
  handlerRef.current = onPaymentMethod;
  const paymentRequest = useMemo(
    () => (stripe ? buildRequest(stripe, { amountCents, wallet }) : null),
    // A new sheet per wallet; the amount is updated below without recreating it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stripe, wallet]
  );

  useEffect(() => {
    if (!paymentRequest) return undefined;
    let active = true;
    paymentRequest.canMakePayment().then((result) => {
      if (active) setAvailable(Boolean(result?.[wallet]));
    }).catch(() => active && setAvailable(false));
    const handler = async (event) => {
      // complete("success") only after Elakay confirmed the authorization.
      const ok = await Promise.resolve(handlerRef.current(event.paymentMethod?.id)).catch(() => false);
      event.complete(ok ? "success" : "fail");
    };
    paymentRequest.on("paymentmethod", handler);
    return () => { active = false; paymentRequest.off?.("paymentmethod", handler); };
  }, [paymentRequest, wallet]);

  useEffect(() => {
    if (paymentRequest && Number(amountCents) > 0) {
      try { paymentRequest.update({ total: { label: "Elakay", amount: Math.round(amountCents) } }); } catch { /* sheet open */ }
    }
  }, [paymentRequest, amountCents]);

  if (available === null) return <div className="elk-pay-hint">{elakayAutoText("h_pay_loading", "Loading...")}</div>;
  if (!available) return <div className="elk-pay-hint elk-pay-hint--warn">{elakayAutoText("h_pay_method_unavailable", "Payment Method Unavailable")}</div>;
  return (
    <PaymentRequestButtonElement
      options={{ paymentRequest, style: { paymentRequestButton: { type: "buy", theme: "dark", height: "46px" } } }}
    />
  );
};

export default WalletPayButton;
